import { ReactNode, useCallback, useEffect, useState } from 'react';

import { useNavigate, useParams } from 'react-router-dom';

import { APIBaseChronos } from '@/api/hosts';
import { useDeleteThread } from '@/api/mutations/useDeleteThread';
import { Thread } from '@/api/types/backend-dto-types';
import useGetFetchConfig from '@/api/useGetFetchConfig';
import formatDate from '@/helpers/formatDate';
import { ConfirmationModal } from '@/new/components/dialogs/confirmation-dialog';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/new/components/shadcn/tooltip';
import { cn } from '@/new/lib/utils';
import { ThreadStatus } from '@/types';

interface PreviousThreadProps {
  activeTab: 'previous' | 'published';
  threadText: string;
  threadId: string;
  goToThread: (threadId: string) => void;
  threadStatus: ThreadStatus;
  isActive: boolean;
  createdAt: string;
  createdByEmail?: string;
  createdById: string;
  userId?: string;
}

const ACTIVE_STATUSES: ThreadStatus[] = [ThreadStatus.PlanExecuting, ThreadStatus.PlanLoading];

const StatusIcon = ({ status, icon }: { status: ThreadStatus; icon: ReactNode }) => {
  return ACTIVE_STATUSES.includes(status) ? (
    <Icon className="lucide--loader-circle animate-spin text-xs text-yellow-500" />
  ) : status === ThreadStatus.PlanLoaded ? (
    <Icon className="lucide--clipboard-list text-xs text-gray-500" />
  ) : status === ThreadStatus.Failed ? (
    <Icon className="lucide--triangle-alert text-xs text-red-500" />
  ) : status === ThreadStatus.PlanComplete && icon ? (
    <div className="animate-popInHalf flex items-center">{icon}</div>
  ) : null;
};

export const PreviousThread = ({
  activeTab,
  threadText,
  threadId,
  goToThread,
  threadStatus,
  createdAt,
  isActive,
  createdByEmail,
  createdById,
  userId,
}: PreviousThreadProps) => {
  const { matterId, subsetId } = useParams();
  if (!matterId || !subsetId) return;

  const [status, setStatus] = useState<ThreadStatus>(threadStatus);
  const [icon, setIcon] = useState<ReactNode>();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const { fetchConfigGET } = useGetFetchConfig();

  const navigate = useNavigate();
  const { mutateAsync: deleteThread } = useDeleteThread(matterId, subsetId);

  const handleConfirmDelete = async () => {
    await deleteThread(threadId);

    if (isActive) {
      navigate(`/matters/${matterId}/subsets/${subsetId}/kim`);
    }
    setShowDeleteModal(false);
  };

  const fetchThread = useCallback(
    async (threadId: string): Promise<Thread | null> => {
      try {
        const response = await fetch(
          `${APIBaseChronos}/api/matter/${matterId}/subset/${subsetId}/thread/${threadId}`,
          fetchConfigGET,
        );
        if (!response.ok) {
          console.error('Fetching thread failed');
          return null;
        }
        return response.json();
      } catch (error) {
        console.error('Fetching thread failed', error);
        return null;
      }
    },
    [matterId, fetchConfigGET],
  );

  useEffect(() => {
    let intervalId: NodeJS.Timeout;

    const pollThread = async (threadId: string) => {
      const response = await fetchThread(threadId);
      if (!response) return;
      const currentStatus: ThreadStatus = response.status;

      if (currentStatus === ThreadStatus.PlanLoaded) {
        setIcon(<Icon className="lucide--clipboard-list text-xs text-gray-500" />);
        setStatus(currentStatus);
      } else if (currentStatus === ThreadStatus.Failed) {
        setIcon(<Icon className="lucide--triangle-alert text-xs text-red-500" />);
        setStatus(currentStatus);
      } else if (currentStatus === ThreadStatus.PlanComplete) {
        setStatus(currentStatus);
        setIcon(<Icon className="lucide--circle-check text-xs text-teal-500" />);
        setTimeout(() => {
          setIcon(undefined);
        }, 2000);
      }
    };

    if (status === ThreadStatus.PlanExecuting || status === ThreadStatus.PlanLoading) {
      intervalId = setInterval(() => {
        pollThread(threadId);
      }, 4000);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [status, threadId, fetchThread]);

  const canDelete = !ACTIVE_STATUSES.includes(status) && createdById === userId;

  return (
    <>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            className={cn(
              `group flex cursor-pointer items-center justify-between rounded px-2 transition-colors duration-150 hover:bg-gray-200`,
              isActive && 'bg-gray-100',
            )}
          >
            <Button
              variant="ghost"
              size="sm"
              className={`cursor-pointer truncate py-2 text-xs`}
              onClick={() => threadId && goToThread(threadId)}
            >
              <p className="overflow-hidden text-xs text-ellipsis whitespace-nowrap text-gray-800">{threadText}</p>
            </Button>

            <StatusIcon status={status} icon={icon} />

            {canDelete && (
              <Button
                variant="ghost"
                className="opacity-0 duration-150 group-focus-within:opacity-100 group-hover:opacity-100"
                size="iconSm"
                onClick={() => setShowDeleteModal(true)}
              >
                <Icon className="lucide--trash-2 text-xs" />
              </Button>
            )}
          </div>
        </TooltipTrigger>
        <TooltipContent side="right" className="w-auto max-w-[600px]">
          <div className="flex flex-col">
            <p className="mb-1 font-semibold">{formatDate(createdAt)}</p>
            {createdByEmail && activeTab === 'published' && <p className="mb-1">Created by {createdByEmail}</p>}
            <p>{threadText}</p>
          </div>
        </TooltipContent>
      </Tooltip>

      <ConfirmationModal
        isOpen={showDeleteModal}
        onOpenChange={setShowDeleteModal}
        title="Delete Thread"
        onConfirm={handleConfirmDelete}
        isLoading={false}
      >
        <p>Are you sure you want to delete this thread?</p>
      </ConfirmationModal>
    </>
  );
};
